/**
 * Subscription Events Store
 *
 * Simulates Stripe webhook events for subscription management.
 * In production, these events would come from a real Stripe webhook endpoint.
 *
 * Supported webhook event types:
 * - customer.subscription.updated
 * - customer.subscription.deleted
 * - customer.subscription.created
 * - invoice.payment_succeeded
 * - invoice.payment_failed
 */

// Storage key for subscription events
const SUBSCRIPTION_EVENTS_KEY = 'photodrop_subscription_events'
const SUBSCRIPTION_STATE_KEY = 'photodrop_subscription_state'

// Stripe-like event types
export type StripeEventType =
  | 'customer.subscription.created'
  | 'customer.subscription.updated'
  | 'customer.subscription.deleted'
  | 'invoice.payment_succeeded'
  | 'invoice.payment_failed'

// Subscription statuses (mirroring Stripe)
export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'trialing'
  | 'incomplete'

// Tier mapping for display
export type SubscriptionTier = 'free' | 'pro' | 'business'

// Webhook event structure
export interface StripeWebhookEvent {
  id: string
  type: StripeEventType
  created: number // Unix timestamp
  data: {
    object: {
      id: string // Subscription ID
      customer: string
      status: SubscriptionStatus
      current_period_start: number
      current_period_end: number
      cancel_at_period_end: boolean
      canceled_at?: number
      plan?: {
        id: string
        nickname: string // 'pro' or 'business'
        amount: number
        interval: 'month' | 'year'
      }
    }
    previous_attributes?: {
      status?: SubscriptionStatus
      cancel_at_period_end?: boolean
    }
  }
}

// Subscription state (what the app tracks)
export interface SubscriptionState {
  subscriptionId?: string
  customerId?: string
  tier: SubscriptionTier
  status: SubscriptionStatus
  currentPeriodStart?: number
  currentPeriodEnd?: number
  cancelAtPeriodEnd: boolean
  canceledAt?: number
  gracePeriodEndsAt?: number // When grace period expires for cancelled subscriptions
  lastEventId?: string
  lastUpdated: number
}

// Event handler callback type
type EventCallback = (event: StripeWebhookEvent, state: SubscriptionState) => void

// Store event listeners
const eventListeners: EventCallback[] = []

// Get stored events
export function getSubscriptionEvents(): StripeWebhookEvent[] {
  try {
    const stored = localStorage.getItem(SUBSCRIPTION_EVENTS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

// Save events
function saveSubscriptionEvents(events: StripeWebhookEvent[]): void {
  localStorage.setItem(SUBSCRIPTION_EVENTS_KEY, JSON.stringify(events))
}

// Get subscription state
export function getSubscriptionState(): SubscriptionState {
  try {
    const stored = localStorage.getItem(SUBSCRIPTION_STATE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Fall through to default
  }

  // Default state (free tier, no subscription)
  return {
    tier: 'free',
    status: 'active',
    cancelAtPeriodEnd: false,
    lastUpdated: Date.now(),
  }
}

// Save subscription state
export function saveSubscriptionState(state: SubscriptionState): void {
  localStorage.setItem(SUBSCRIPTION_STATE_KEY, JSON.stringify(state))

  // Broadcast state change via storage event for cross-tab sync
  window.dispatchEvent(new StorageEvent('storage', {
    key: SUBSCRIPTION_STATE_KEY,
    newValue: JSON.stringify(state),
  }))
}

// Subscribe to subscription events
export function subscribeToSubscriptionEvents(callback: EventCallback): () => void {
  eventListeners.push(callback)

  return () => {
    const index = eventListeners.indexOf(callback)
    if (index > -1) {
      eventListeners.splice(index, 1)
    }
  }
}

// Notify all listeners
function notifyListeners(event: StripeWebhookEvent, state: SubscriptionState): void {
  eventListeners.forEach(callback => {
    try {
      callback(event, state)
    } catch (err) {
      console.error('[Webhook] Error in event listener:', err)
    }
  })
}

/**
 * Process a Stripe webhook event
 * This is the main entry point for handling webhook events
 */
export function processWebhookEvent(event: StripeWebhookEvent): SubscriptionState {
  console.log(`[Webhook] Received event: ${event.type}`, event)

  // Store the event
  const events = getSubscriptionEvents()
  events.unshift(event) // Add to beginning
  if (events.length > 100) {
    events.pop() // Keep only last 100 events
  }
  saveSubscriptionEvents(events)

  // Get current state
  let state = getSubscriptionState()

  // Process based on event type
  switch (event.type) {
    case 'customer.subscription.created':
      state = handleSubscriptionCreated(event, state)
      break

    case 'customer.subscription.updated':
      state = handleSubscriptionUpdated(event, state)
      break

    case 'customer.subscription.deleted':
      state = handleSubscriptionDeleted(event, state)
      break

    case 'invoice.payment_succeeded':
      state = handlePaymentSucceeded(event, state)
      break

    case 'invoice.payment_failed':
      state = handlePaymentFailed(event, state)
      break
  }

  // Update last event tracking
  state.lastEventId = event.id
  state.lastUpdated = Date.now()

  // Save and notify
  saveSubscriptionState(state)
  notifyListeners(event, state)

  console.log(`[Webhook] Processed event, new state:`, state)

  return state
}

// Handle subscription.created event
function handleSubscriptionCreated(event: StripeWebhookEvent, state: SubscriptionState): SubscriptionState {
  const sub = event.data.object
  const tier = mapPlanToTier(sub.plan?.nickname)

  return {
    ...state,
    subscriptionId: sub.id,
    customerId: sub.customer,
    tier,
    status: sub.status,
    currentPeriodStart: sub.current_period_start,
    currentPeriodEnd: sub.current_period_end,
    cancelAtPeriodEnd: sub.cancel_at_period_end,
    gracePeriodEndsAt: undefined,
  }
}

// Handle subscription.updated event
function handleSubscriptionUpdated(event: StripeWebhookEvent, state: SubscriptionState): SubscriptionState {
  const sub = event.data.object
  const prevAttrs = event.data.previous_attributes
  const tier = mapPlanToTier(sub.plan?.nickname) || state.tier

  const newState: SubscriptionState = {
    ...state,
    subscriptionId: sub.id,
    customerId: sub.customer,
    tier,
    status: sub.status,
    currentPeriodStart: sub.current_period_start,
    currentPeriodEnd: sub.current_period_end,
    cancelAtPeriodEnd: sub.cancel_at_period_end,
  }

  // If cancel_at_period_end changed to true, set grace period
  if (sub.cancel_at_period_end && !prevAttrs?.cancel_at_period_end) {
    newState.canceledAt = sub.canceled_at || Date.now() / 1000
    // Grace period until end of current billing period
    newState.gracePeriodEndsAt = sub.current_period_end * 1000
    console.log(`[Webhook] Subscription set to cancel at period end. Grace period until: ${new Date(newState.gracePeriodEndsAt)}`)
  }

  // If cancellation was reverted
  if (!sub.cancel_at_period_end && prevAttrs?.cancel_at_period_end === true) {
    newState.canceledAt = undefined
    newState.gracePeriodEndsAt = undefined
    console.log('[Webhook] Subscription cancellation reverted')
  }

  return newState
}

// Handle subscription.deleted event
function handleSubscriptionDeleted(event: StripeWebhookEvent, state: SubscriptionState): SubscriptionState {
  const sub = event.data.object

  console.log('[Webhook] Subscription deleted, reverting to free tier')

  return {
    ...state,
    subscriptionId: undefined,
    tier: 'free',
    status: 'canceled',
    cancelAtPeriodEnd: false,
    canceledAt: sub.canceled_at || Date.now() / 1000,
    gracePeriodEndsAt: undefined,
    currentPeriodStart: undefined,
    currentPeriodEnd: undefined,
  }
}

// Handle invoice.payment_succeeded event
function handlePaymentSucceeded(_event: StripeWebhookEvent, state: SubscriptionState): SubscriptionState {
  console.log('[Webhook] Payment succeeded, subscription is active')

  // Payment succeeded - ensure subscription is active
  if (state.status === 'past_due' || state.status === 'unpaid') {
    return {
      ...state,
      status: 'active',
    }
  }

  return state
}

// Handle invoice.payment_failed event
function handlePaymentFailed(_event: StripeWebhookEvent, state: SubscriptionState): SubscriptionState {
  console.log('[Webhook] Payment failed, subscription is past_due')

  // Payment failed - mark as past_due
  return {
    ...state,
    status: 'past_due',
  }
}

// Map Stripe plan nickname to our tier
function mapPlanToTier(planNickname?: string): SubscriptionTier {
  if (!planNickname) return 'free'

  const lowerName = planNickname.toLowerCase()
  if (lowerName.includes('business')) return 'business'
  if (lowerName.includes('pro')) return 'pro'
  return 'free'
}

/**
 * Simulate a Stripe webhook event
 * This is for demo/testing purposes
 */
export function simulateWebhookEvent(
  type: StripeEventType,
  options: {
    tier?: SubscriptionTier
    status?: SubscriptionStatus
    cancelAtPeriodEnd?: boolean
    billingInterval?: 'month' | 'year'
  } = {}
): StripeWebhookEvent {
  const now = Math.floor(Date.now() / 1000)
  const state = getSubscriptionState()

  // Generate subscription ID if not exists
  const subscriptionId = state.subscriptionId || `sub_${crypto.randomUUID().slice(0, 14)}`
  const customerId = state.customerId || `cus_${crypto.randomUUID().slice(0, 14)}`

  // Calculate billing period
  const interval = options.billingInterval || 'month'
  const periodLength = interval === 'year' ? 365 * 24 * 60 * 60 : 30 * 24 * 60 * 60
  const currentPeriodStart = now
  const currentPeriodEnd = now + periodLength

  // Determine tier and amount
  const tier = options.tier || state.tier
  const amount = tier === 'business' ? 4900 : tier === 'pro' ? 1200 : 0

  // Build the event
  const event: StripeWebhookEvent = {
    id: `evt_${crypto.randomUUID().slice(0, 14)}`,
    type,
    created: now,
    data: {
      object: {
        id: subscriptionId,
        customer: customerId,
        status: options.status || (type === 'customer.subscription.deleted' ? 'canceled' : 'active'),
        current_period_start: currentPeriodStart,
        current_period_end: currentPeriodEnd,
        cancel_at_period_end: options.cancelAtPeriodEnd || false,
        canceled_at: type === 'customer.subscription.deleted' ? now : undefined,
        plan: amount > 0 ? {
          id: `plan_${tier}`,
          nickname: tier,
          amount,
          interval,
        } : undefined,
      },
    },
  }

  // Add previous_attributes for updates
  if (type === 'customer.subscription.updated') {
    event.data.previous_attributes = {
      status: state.status,
      cancel_at_period_end: state.cancelAtPeriodEnd,
    }
  }

  return event
}

/**
 * Simulate and process a webhook event in one call
 * Convenience method for demo purposes
 */
export function simulateAndProcessWebhook(
  type: StripeEventType,
  options: {
    tier?: SubscriptionTier
    status?: SubscriptionStatus
    cancelAtPeriodEnd?: boolean
    billingInterval?: 'month' | 'year'
  } = {}
): SubscriptionState {
  const event = simulateWebhookEvent(type, options)
  return processWebhookEvent(event)
}

/**
 * Check if grace period has expired and apply downgrade if needed
 * Call this periodically or on app load
 */
export function checkGracePeriodExpiry(): SubscriptionState | null {
  const state = getSubscriptionState()

  if (state.gracePeriodEndsAt && Date.now() > state.gracePeriodEndsAt) {
    console.log('[Webhook] Grace period expired, applying downgrade')

    // Generate a subscription.deleted event
    const event = simulateWebhookEvent('customer.subscription.deleted', {
      status: 'canceled',
    })

    return processWebhookEvent(event)
  }

  return null
}

/**
 * Clear all subscription events and reset state
 * For testing purposes
 */
export function clearSubscriptionData(): void {
  localStorage.removeItem(SUBSCRIPTION_EVENTS_KEY)
  localStorage.removeItem(SUBSCRIPTION_STATE_KEY)
}

/**
 * Sync subscription state with auth context
 * Returns the tier that should be set in the auth context
 */
export function getEffectiveTier(): SubscriptionTier {
  const state = getSubscriptionState()

  // Check grace period expiry
  if (state.gracePeriodEndsAt && Date.now() > state.gracePeriodEndsAt) {
    return 'free'
  }

  // If subscription is canceled but within grace period, keep the tier
  if (state.cancelAtPeriodEnd && state.gracePeriodEndsAt && Date.now() < state.gracePeriodEndsAt) {
    return state.tier
  }

  // If status is canceled, return free
  if (state.status === 'canceled') {
    return 'free'
  }

  return state.tier
}
